<template>
	<div
		v-if="
			isBuilding(entity.frameName) ||
			isRoad(entity.frameName) ||
			tilerepoSalvageableFrameNames.has(entity.frameName)
		"
		class="tile-aggregation-details-wrapper"
		:class="{
			'tile-aggregation-details-wrapper--is-tooltip': isTooltip,
		}"
	>
		<div
			class="tile-aggregation-details"
			data-scope="population"
			v-if="populationIncome && populationIncome.size > 0"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.accomodates") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					<AppPrice
						v-for="[currency, value] in populationIncome"
						:key="currency"
						:amount="+value"
						:currency="resourceTypeToCurrencyType(currency)"
						:resourceCheck="false"
						:iconSize="14"
					/>
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="production"
			v-if="
				(production && production.size > 0) ||
				entity.frameName === FrameName.GEMS_MINE
			"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.produces") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					<template v-if="entity.frameName === FrameName.GEMS_MINE">
						<AppPrice
							:amount="
								(entity as GemsMineBuildingTileEntity)
									.gemsProductionWithUpgrade
							"
							:currency="
								resourceTypeToCurrencyType(ResourceType.GEMS)
							"
							:resourceCheck="false"
							:iconSize="14"
							:fontSize="14"
						/>
						<span class="tile-aggregation-details__value-unit">
							{{ `/ ${translate("ui.day")}` }}
						</span>
					</template>
					<template
						v-else-if="isPromotion && entity.productionDefaultData"
					>
						<div
							v-for="{
								number,
								icon,
								label,
							} in entity.productionDefaultData"
							:key="icon"
							class="tile-aggregation-details__value-item"
						>
							<AppPrice
								:amount="+number"
								:currency="resourceTypeToCurrencyType(icon)"
								:resourceCheck="false"
								:iconSize="14"
								:fontSize="14"
							/>
							<span
								v-if="label"
								class="tile-aggregation-details__value-unit"
							>
								/ {{ translate(`${label}.name`) }}
							</span>
						</div>
					</template>
					<template v-else>
						<div
							v-for="(
								[currency, value], index
							) in productionEntries"
							:key="currency"
							class="tile-aggregation-details__value-item"
						>
							<AppPrice
								:amount="+value"
								:currency="resourceTypeToCurrencyType(currency)"
								:resourceCheck="false"
								:iconSize="14"
								:fontSize="14"
							/>
							<span
								v-if="
									![ResourceType.FOOD_INCOME].includes(
										currency,
									)
								"
								class="tile-aggregation-details__value-unit"
							>
								{{ `/${gameTickerIntervalInSeconds}s` }}
							</span>
							<span
								v-if="index < productionEntries.length - 1"
								class="tile-aggregation-details__value-comma"
							>
								&#44;
							</span>
						</div>
					</template>
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="population-consumption"
			v-if="
				populationConsumption &&
				populationConsumption.size > 0 &&
				!isUpgrade
			"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.employs") }}:
			</span>
			<div class="tile-aggregation-details__value">
				<AppPrice
					v-for="[currency, value] in populationConsumption"
					:key="currency"
					:amount="+value"
					:currency="resourceTypeToCurrencyType(currency)"
					:resourceCheck="false"
					:iconSize="14"
				/>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="consumption"
			v-if="consumption && consumption.size > 0"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.consumes") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value-item">
					<AppPrice
						v-for="[currency, value] in consumption"
						:key="currency"
						:amount="+value"
						:currency="resourceTypeToCurrencyType(currency)"
						:resourceCheck="false"
						:iconSize="14"
						:fontSize="14"
					/>
					<span class="tile-aggregation-details__value-unit">
						{{ `/${gameTickerIntervalInSeconds}s` }}
					</span>
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="storage"
			v-if="storage && storage.size > 0"
		>
			<span
				v-if="
					tilerepoAccommodationBuildingsFrameNames.has(
						entity.frameName,
					)
				"
				class="tile-aggregation-details__label"
			>
				{{ translate("ui.accomodates") }}:
			</span>
			<span v-else class="tile-aggregation-details__label">
				{{ translate("ui.stores") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					<AppPrice
						v-for="[currency, value] in storage"
						:key="currency"
						:amount="value"
						:currency="resourceTypeToCurrencyType(currency)"
						:resourceCheck="false"
						:iconSize="14"
						:fontSize="14"
					/>
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="range"
			v-if="
				range &&
				!tilerepoHideRangeIndicatorsFrameNames.has(entity.frameName)
			"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.effective_range") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					{{ range }}
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="fishing-boat-count"
			v-if="tilerepoShipyardFrameNames.has(entity.frameName)"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.fishing_boats") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					{{ (entity as ShipyardBuildingTileEntity).fishingBoats }}
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="sea-exploration-range"
			v-if="tilerepoShipyardFrameNames.has(entity.frameName)"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.sea_exploration_range") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					+{{
						(entity as ShipyardBuildingTileEntity)
							.seaExplorationPower
					}}
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="shipyard-range-total"
			v-if="tilerepoShipyardFrameNames.has(entity.frameName)"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.shipyard_range_total") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					{{
						(entity as ShipyardBuildingTileEntity)
							.maxSeaExplorationPower
					}}
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="road-limit"
			v-if="roadLimit"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.limit") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					<span
						:class="{
							'tile-aggregation-details__value-negative':
								roadLimit.result >= roadLimit.cap,
						}"
					>
						{{ `${roadLimit.result} / ${roadLimit.cap}` }}
					</span>
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="food-consumption"
			v-if="foodConsumption?.[1]"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.consumes") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					<AppPrice
						v-if="foodConsumption"
						:amount="+foodConsumption[1]"
						:currency="
							resourceTypeToCurrencyType(foodConsumption[0])
						"
						:resourceCheck="false"
						:iconSize="14"
					/>
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="exploration-speed"
			v-if="explorationSpeed !== null"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.exploration_speed") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					+{{ Object.values(explorationSpeed)[1] }}
					<div class="icon icon-explorationspeed-14" />
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details"
			data-scope="siege-attributes"
			v-if="isFriendlyMilitary"
		>
			<div class="siege-attributes">
				<div class="siege-attribute">
					<span class="siege-attribute-label">
						{{ translate("ui.health") }}:
					</span>
					<div class="siege-attribute-info">
						<span class="siege-attribute-value">{{
							entity.siegeAttributes?.maxHp
						}}</span>
						<AppMainIcon
							class="armor-badge__icon"
							iconName="hearth"
							:iconSize="16"
						/>
					</div>
				</div>
				<div class="siege-attribute">
					<span class="siege-attribute-label">
						{{ translate("ui.armor") }}:
					</span>
					<div class="siege-attribute-info">
						<span class="siege-attribute-value">{{
							entity.siegeAttributes?.armor
						}}</span>
						<AppMainIcon
							class="armor-badge__icon"
							iconName="blue-shield"
							:iconSize="16"
						/>
					</div>
				</div>
				<div class="siege-attribute">
					<span class="siege-attribute-label">
						{{ translate("ui.attack") }}:
					</span>
					<div class="siege-attribute-info">
						<span class="siege-attribute-value">{{
							attackValue
						}}</span>
						<AppMainIcon
							class="armor-badge__icon"
							iconName="swords"
							:iconSize="16"
						/>
					</div>
				</div>
			</div>
		</div>
		<div
			class="tile-aggregation-details highlighted"
			data-scope="supply-limit"
			v-if="isFriendlyMilitary"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.attack_type") }}:
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					{{ attackType }}
				</div>
			</div>
		</div>
		<div
			v-if="grantsBuff.length"
			class="tile-aggregation-details highlighted"
		>
			<div
				v-for="(buff, i) in grantsBuff"
				:key="i"
				class="tile-aggregation-details__label__line"
			>
				<span class="tile-aggregation-details__label__line__pretext">
					{{ buffTextData(buff).pretext }}
				</span>

				<Tooltip
					:placement="Placements.TOP"
					v-if="buffIcon(buff).length > 0"
				>
					<template #element>
						<AppMainIcon
							v-if="buffIcon(buff).length > 0"
							:iconName="buffIcon(buff)"
							:iconSize="16"
						/>
					</template>
					<template #tooltip>
						<span>
							{{ translate(buffTooltip(buff)) }}
						</span>
					</template>
				</Tooltip>
				<span class="tile-aggregation-details__label__line__posttext">
					{{ buffTextData(buff).posttext }}
				</span>
				<!-- {{
					translate(`ui.buff_${buff.type}`)
						.replace("{number}", buff.value.toString())
						.replace("{range}", "1")
				}} -->
			</div>
		</div>
		<div
			v-if="isFriendlySupply"
			class="tile-aggregation-details highlighted"
			data-scope="no-target"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.no_target") }}
			</span>
		</div>
		<div
			v-if="entity.siegeAttributes?.supplyLimit !== undefined"
			class="tile-aggregation-details highlighted"
			data-scope="supply-limit"
		>
			<span class="tile-aggregation-details__label">
				{{ translate("ui.supply_limit") }}
			</span>
			<div>
				<div class="tile-aggregation-details__value">
					{{ entity.siegeAttributes?.supplyLimit > 0 ? "+" : ""
					}}{{ entity.siegeAttributes?.supplyLimit }}
				</div>
			</div>
		</div>

		<div v-if="entity.rewardRange.size > 0">
			{{ translate("ui.you_get") }}
			<div
				v-for="[resourceType, reward] in entity.rewardRange"
				:key="resourceType"
				class="tile-aggregation-details__reward"
			>
				(
				<div>{{ getRewardText(reward) }}</div>
				<AppMainIcon
					v-if="resourceType"
					:iconName="resourceType"
					:iconSize="14"
					:style="{ marginTop: `${14 / 8 / 16}rem` }"
				/>)
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppPrice from "@/components/AppPrice.vue";
import Tooltip from "@/components/Tooltip.vue";
import { GAME_TICKER_INTERVAL } from "@/config/constants";
import {
	RoyalProvisionRewardKey,
	royalProvisionRewards,
} from "@/game/config/shop";

import type GemsMineBuildingTileEntity from "@/game/entities/buildings/GemsMineBuildingTileEntity";
import type ShipyardBuildingTileEntity from "@/game/entities/buildings/ShipyardBuildingTileEntity";
import {
	FrameName,
	tilerepoAccommodationBuildingsFrameNames,
	tilerepoCobbledRoadsFrameNames,
	tilerepoFriendlyMilitaryUnitsFrameNames,
	tilerepoFriendlySupplyUnitsFrameNames,
	tilerepoGravelRoadsFrameNames,
	tilerepoHideRangeIndicatorsFrameNames,
	tilerepoSalvageableFrameNames,
	tilerepoShipyardFrameNames,
	tilerepoStoneBridgeRoadsFrameNames,
	tilerepoTunnelRoadsFrameNames,
	tilerepoWoodenBridgeRoadsFrameNames,
} from "@/game/entities/config";
import { isBuilding } from "@/game/entities/config/buildings";
import { ResourceType } from "@/game/entities/config/resources";
import { isRoad } from "@/game/entities/config/roads";
import type TileEntity from "@/game/entities/TileEntity";
import { buffIcon, buffTextData, buffTooltip } from "@/game/utils/fightsystem";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useTileRepository from "@/hooks/useTileRepository";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUserStore from "@/store/useUserStore";
import type { ArmorOrAttackObject, Buff } from "@/types/tile";
import { type ProductionValue } from "@/types/tile";
import { Placements } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { ceilNumber, floorNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import { computed, markRaw } from "vue";

interface Props {
	entity: TileEntity;
	isUpgrade?: boolean;
	isPromotion?: boolean;
	isTooltip?: boolean;
}

const tilerepo = markRaw(useTileRepository());
const ssr = markRaw(useSiegeSystemRepository());

const settlementsStore = useSettlementsStore();
const userStore = useUserStore();

const { entity, isUpgrade = false, isPromotion = false } = defineProps<Props>();

const gameTickerIntervalInSeconds = GAME_TICKER_INTERVAL / 1_000;

// Cache and pre-compute all filtered data in a single computation
const cachedEntityData = computed(
	(): {
		production: Map<ResourceType, ProductionValue> | null;
		populationIncome: Map<ResourceType, ProductionValue> | null;
		consumption: Map<ResourceType, number> | null;
		populationConsumption: Map<ResourceType, ProductionValue> | null;
		foodConsumption: [ResourceType, number] | null;
		explorationSpeed: [ResourceType, ProductionValue] | null;
	} => {
		const productionArray = Array.from(entity.production) || [];
		const consumptionArray = Array.from(entity.consumption) || [];

		// Calculate Exploring Power Boost multiplier based on active provisions
		const activeProvisionsCount = userStore.activeProvisions.length;
		let explorationMultiplier = 1;

		if (activeProvisionsCount > 0) {
			const configBonus =
				royalProvisionRewards[
					RoyalProvisionRewardKey.ExploringPowerBoost
				].value ?? 1;
			const bonusPerPackage = configBonus - 1;
			explorationMultiplier = 1 + bonusPerPackage * activeProvisionsCount;
		}

		return {
			production: productionArray.length
				? new Map<ResourceType, ProductionValue>(
						productionArray.filter(
							([key]: [ResourceType, ProductionValue]): boolean =>
								key.endsWith("Income") &&
								![ResourceType.POPULATION_INCOME].includes(key),
						),
					)
				: null,

			populationIncome: productionArray.length
				? new Map<ResourceType, ProductionValue>(
						productionArray.filter(
							([key]: [ResourceType, ProductionValue]): boolean =>
								key === ResourceType.POPULATION_INCOME,
						),
					)
				: null,

			consumption: consumptionArray.length
				? new Map<ResourceType, number>(
						consumptionArray.filter(
							([key]: [ResourceType, number]): boolean =>
								key.endsWith("Consumption") &&
								![
									ResourceType.FOOD_CONSUMPTION,
									ResourceType.POPULATION_CONSUMPTION,
								].includes(key),
						),
					)
				: null,

			populationConsumption: consumptionArray.length
				? new Map<ResourceType, ProductionValue>(
						consumptionArray.filter(
							([key]: [ResourceType, ProductionValue]): boolean =>
								key === ResourceType.POPULATION_CONSUMPTION,
						),
					)
				: null,
			foodConsumption: consumptionArray.length
				? (consumptionArray.find(
						([key]: [ResourceType, ProductionValue]): boolean =>
							key === ResourceType.FOOD_CONSUMPTION,
					) ?? null)
				: null,
			explorationSpeed: productionArray.length
				? ((): [ResourceType, ProductionValue] | null => {
						const found = productionArray.find(
							([key]: [ResourceType, ProductionValue]): boolean =>
								key === ResourceType.EXPLORATION_SPEED,
						);
						if (!found) return null;
						// Apply linear scaling provision multiplier to exploration speed value
						// NOTE: Used floor instead of ceil per specification
						const scaledValue = floorNumber(
							+found[1] * explorationMultiplier,
						);
						return [found[0], scaledValue] as [
							ResourceType,
							ProductionValue,
						];
					})()
				: null,
		};
	},
);

const getRewardText = (reward: { min: number; max: number }): string => {
	if (reward.min === reward.max) return formatNumber(reward.min);
	return `${formatNumber(reward.min)} - ${formatNumber(reward.max)}`;
};

// NOTE: produces ... OK
const production = computed(
	(): Map<ResourceType, ProductionValue> | null =>
		cachedEntityData.value.production,
);

const populationIncome = computed(
	(): Map<ResourceType, ProductionValue> | null =>
		cachedEntityData.value.populationIncome,
);

const consumption = computed(
	(): Map<ResourceType, number> | null => cachedEntityData.value.consumption,
);

const populationConsumption = computed(
	(): Map<ResourceType, ProductionValue> | null =>
		cachedEntityData.value.populationConsumption,
);

const foodConsumption = computed(
	(): [ResourceType, ProductionValue] | null =>
		cachedEntityData.value.foodConsumption,
);

const explorationSpeed = computed(
	(): [ResourceType, ProductionValue] | null =>
		cachedEntityData.value.explorationSpeed,
);

const isFriendlySupply = computed((): boolean => {
	return tilerepoFriendlySupplyUnitsFrameNames.has(entity.frameName);
});

const isFriendlyMilitary = computed((): boolean => {
	return tilerepoFriendlyMilitaryUnitsFrameNames.has(entity.frameName);
});

const attackValue = computed((): number => {
	const attackObject = entity.siegeAttributes?.attack as ArmorOrAttackObject;
	if (!attackObject) return 0;

	return attackObject.melee + attackObject.ranged + attackObject.siege;
});

const attackType = computed((): string | null => {
	const attackObject = entity.siegeAttributes?.attack as ArmorOrAttackObject;
	if (!attackObject) return null;

	if (attackObject.melee > 0) return translate("ui.melee");
	if (attackObject.ranged > 0) return translate("ui.ranged");
	if (attackObject.siege > 0) return translate("ui.siege");

	return null;
});

const grantsBuff = computed<Array<Buff>>(
	(): Array<Buff> => ssr.getGrantBuffsByTileIndex(entity.index) ?? [],
);

const roadLimit = computed((): { result: number; cap: number } | null => {
	const isGravelType =
		tilerepoGravelRoadsFrameNames.has(entity.frameName) ||
		tilerepoWoodenBridgeRoadsFrameNames.has(entity.frameName);

	const isCobbledType =
		tilerepoCobbledRoadsFrameNames.has(entity.frameName) ||
		tilerepoTunnelRoadsFrameNames.has(entity.frameName) ||
		tilerepoStoneBridgeRoadsFrameNames.has(entity.frameName);

	if (!isGravelType && !isCobbledType) return null;

	const returnValue = { result: 0, cap: 0 };

	const result = tilerepo.getCurrentNumberOfPremiumRoads();
	const caps = settlementsStore.getTotalAvailableRoadCaps;

	if (isGravelType) {
		returnValue.result = result[FrameName.GRAVEL_ROAD];
		returnValue.cap = caps[FrameName.GRAVEL_ROAD];
		return returnValue;
	}

	if (isCobbledType) {
		returnValue.result = result[FrameName.COBBLED_ROAD];
		returnValue.cap = caps[FrameName.COBBLED_ROAD];
		return returnValue;
	}

	return null;
});

const storage = computed((): Map<ResourceType, number> | null => {
	if (!entity?.storage || entity.storage.size === 0) return null;

	const activeProvisionsCount = userStore.activeProvisions.length;
	if (activeProvisionsCount === 0) {
		return entity.storage;
	}

	// Get provision config bonus for storage boost (e.g. 1.3)
	const configBonus =
		royalProvisionRewards[RoyalProvisionRewardKey.InventoryStorageBoost]
			.value ?? 1;
	const bonusPerPackage = configBonus - 1;
	const totalMultiplier = 1 + bonusPerPackage * activeProvisionsCount;

	// Apply multiplier to each storage resource value
	const scaledStorage = new Map<ResourceType, number>();
	for (const [resource, baseValue] of entity.storage.entries()) {
		scaledStorage.set(resource, ceilNumber(baseValue * totalMultiplier));
	}

	return scaledStorage;
});

// NOTE: effective range ...
const range = computed((): number | null =>
	entity?.range ? entity.range : null,
);

const productionEntries = computed(
	(): Array<[ResourceType, ProductionValue]> =>
		Array.from(production.value?.entries() ?? []),
);
</script>

<style lang="scss" scoped>
.tile-aggregation-details-wrapper {
	&--is-tooltip {
		.highlighted {
			color: inherit !important;
		}
	}
}
.tile-aggregation-details {
	@include flex-row-start;
	flex-wrap: wrap;
	align-items: baseline; // tile detail OK, popup overview OK, lvlUp unlock OK
	gap: 0.25rem;
	font-size: 0.75rem;

	&-wrapper {
		width: 100%;
		@include flex-col-start;
		align-items: flex-start;
	}

	:deep(.price__value) {
		font-weight: 500;
		font-size: 0.75rem;
	}

	&__value {
		@include flex-row-start;
		flex-wrap: wrap;
		gap: 0.25rem;

		&-item {
			@include flex-row-start;
			flex-wrap: wrap;
			align-items: baseline;
		}

		&-unit {
			margin-left: 0.063rem;
		}

		&-negative {
			color: #ff3d3d;
		}
	}
	&__reward {
		@include flex-row-start;
		gap: 0.25rem;
		font-weight: bold;
		font-size: 1rem;
	}
	&.highlighted {
		color: #f4d278;
		// & > span {
		// 	color: #f4d278;
		// }
	}
	.siege-attributes {
		@include flex-row-center;
		flex-wrap: wrap;
		justify-content: start;
		gap: 0.25rem;
		.siege-attribute {
			@include flex-row-center;
			position: relative;
			gap: 0.15rem;
			border-top: 0.125rem solid #0d1e34;
			border-right: 0.125rem solid #122b46;
			border-bottom: 0.125rem solid #465d77;
			border-left: 0.125rem solid #112945;
			background-color: #19395e;
			padding: 0 0.15rem 0.125rem;
			min-width: 5rem;
			font-weight: bold;
			font-size: 1.25rem;
			line-height: 1;
			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0.6;
				z-index: 0;
				// NOTE: Color-burn blending is better for dark colors
				mix-blend-mode: color-burn;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				height: 100%;
				content: "";
			}
			&-info {
				@include flex-row-center;
				gap: 0.15rem;
				padding-left: 0.3rem;
				width: 100%;
			}
			&-label {
				flex: 1 1 auto;
				margin-bottom: 0.25rem;
				width: 100%;
				color: #f2dcb5;
				font-size: 0.75rem;
				text-align: center;
			}
			&-value {
				margin-bottom: 0.25rem;
				color: #f4d278;
				font-weight: bold;
				font-size: 0.906rem;
				text-shadow: 0.04rem 0.04rem 0.06rem rgba(0, 0, 0, 0.6);
			}
		}
	}

	&__label {
		&__line {
			@include flex-row-center;
			gap: 0.25rem;

			&__pretext {
				font-weight: 800;
				font-size: 0.9063rem;
			}
			&__posttext {
				font-size: 0.8125rem;
			}
		}
	}
}
</style>
